import type { StatusTone } from "../../components/ui";
import type domains from "../../locales/en/domains.json";
import type { RegisteredDomainStatus } from "../../lib/types";

export interface DomainStatus {
  tone: StatusTone;
  /** The state's name under `domains.status`; the words live with the copy. */
  key: keyof typeof domains.status;
}

/**
 * How a domain's status reads. One map for the list and the detail page, so
 * a row and the page behind it never disagree about how a state looks.
 */
const STATUS: Record<RegisteredDomainStatus, DomainStatus> = {
  pending: { tone: "warn", key: "pending" },
  active: { tone: "good", key: "active" },
  failed: { tone: "bad", key: "failed" },
  expired: { tone: "bad", key: "expired" },
  deleted: { tone: "neutral", key: "deleted" },
};

export function domainStatus(status: RegisteredDomainStatus): DomainStatus {
  return STATUS[status];
}

/** `example.com` → `{ name, extension }`, or null when it is not two parts. */
export function splitDomainName(raw: string): { name: string; extension: string } | null {
  const fqdn = raw.trim().toLowerCase().replace(/\.$/, "");
  const dot = fqdn.indexOf(".");
  if (dot <= 0 || dot === fqdn.length - 1) return null;
  return { name: fqdn.slice(0, dot), extension: fqdn.slice(dot + 1) };
}

/** Days until an ISO date, floored; negative once it has passed. */
export function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  return Math.floor((Date.parse(iso) - Date.now()) / 86_400_000);
}

/** How much of the registration has elapsed, 0–1, for a bar. */
export function yearFraction(registeredAt: string | null, renewalDate: string): number {
  const end = Date.parse(renewalDate);
  const start = registeredAt ? Date.parse(registeredAt) : end - 365 * 86_400_000;
  return Math.min(1, Math.max(0, (Date.now() - start) / Math.max(1, end - start)));
}
